// src/api/config.js
// Works with Vite (import.meta.env) and CRA (process.env)

const host = (typeof window !== "undefined" && window.location.hostname) || "";
const LOCAL_PREVIEW_BUILD = process.env.REACT_APP_PWG_PRODUCTION_RELEASE !== "1";
const isLocalHost = LOCAL_PREVIEW_BUILD && /^(localhost|127\.0\.0\.1)$/.test(host);
const isProdHost = /(?:\.netlify\.app|playswithguru\.com)$/.test(host);
const DEPLOYED_BACKEND = "https://props-backend.onrender.com";

// Safe env getter (Vite first, then CRA)
const getEnv = (k) => {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env && k in import.meta.env) {
      return import.meta.env[k] ?? "";
    }
  } catch {}
  // CRA's DefinePlugin replaces literal process.env.REACT_APP_* references;
  // dynamic process.env[k] lookups are not available in the browser bundle.
  const craEnv = {
    REACT_APP_DATA_BASE: process.env.REACT_APP_DATA_BASE,
    REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
    REACT_APP_STRIPE_BASE: process.env.REACT_APP_STRIPE_BASE,
    REACT_APP_API_BASE: process.env.REACT_APP_API_BASE,
    REACT_APP_SECURE_SPORTS_API_BASE: process.env.REACT_APP_SECURE_SPORTS_API_BASE,
  };
  return craEnv[k] || "";
};

// Optional env overrides (useful for preview/staging)
const DATA_BASE_ENV =
  (getEnv("VITE_DATA_BASE") ||
    getEnv("REACT_APP_DATA_BASE") ||
    getEnv("REACT_APP_BACKEND_URL") || // legacy
    "").trim();

const STRIPE_BASE_ENV =
  (getEnv("VITE_STRIPE_BASE") ||
    getEnv("REACT_APP_STRIPE_BASE") ||
    getEnv("VITE_API_BASE") ||          // legacy single-base var
    getEnv("REACT_APP_API_BASE") ||     // legacy single-base var
    "").trim();

const SECURE_SPORTS_BASE_ENV =
  (getEnv("VITE_SECURE_SPORTS_API_BASE") ||
    getEnv("REACT_APP_SECURE_SPORTS_API_BASE") ||
    "").trim();

// --- Defaults by environment (use underscored internals to avoid name clashes) ---
let _DATA_BASE;   // for props/data endpoints: /mlb-props, /nba-props, etc. (NO /api)
let _STRIPE_BASE; // for subscription endpoints: /api/create-checkout-session, etc.

if (isProdHost) {
  // On Netlify / playswithguru.com
  _DATA_BASE = "";        // same-origin (site root)
  _STRIPE_BASE = "/api";  // site-relative proxy to backend's /api/*
} else if (isLocalHost) {
  // Local UI testing uses the deployed authenticated backend by default.
  // Port 5050 is also used by THEO locally and is not a safe implicit API
  // target. Developers can still opt into a local Flask backend explicitly
  // through REACT_APP_DATA_BASE / REACT_APP_STRIPE_BASE.
  _DATA_BASE = DEPLOYED_BACKEND;
  _STRIPE_BASE = `${DEPLOYED_BACKEND}/api`;
} else {
  // Other hosts (e.g., preview)
  _DATA_BASE = "";       // same-origin by default
  _STRIPE_BASE = "/api"; // proxy for /api/*
}

// Apply env overrides if provided
if (DATA_BASE_ENV) _DATA_BASE = DATA_BASE_ENV;
if (STRIPE_BASE_ENV) _STRIPE_BASE = STRIPE_BASE_ENV;

// Safety: never point to localhost when on a prod host
const isLocalUrl = LOCAL_PREVIEW_BUILD
  ? (u) => /^https?:\/\/localhost(?::\d+)?/i.test(u) || /^https?:\/\/127\.0\.0\.1(?::\d+)?/i.test(u)
  : () => false;

// API_BASE/STRIPE_BASE is the root for authenticated Flask routes. Older
// environments sometimes set REACT_APP_API_BASE to the bare backend origin;
// normalize that value so callers cannot accidentally request /premium/*
// instead of the backend's actual /api/premium/* route.
const normalizeApiBase = (value) => {
  const base = String(value || "").trim().replace(/\/+$/, "");
  if (!base) return "/api";
  return /\/api$/i.test(base) ? base : `${base}/api`;
};

if (isProdHost) {
  if (isLocalUrl(_DATA_BASE)) _DATA_BASE = "";
  if (isLocalUrl(_STRIPE_BASE)) _STRIPE_BASE = "/api";
}

_STRIPE_BASE = normalizeApiBase(_STRIPE_BASE);

// Exports
export const DATA_BASE = _DATA_BASE || "";
export const STRIPE_BASE = _STRIPE_BASE || "/api";

// Back-compat: legacy code that imports API_BASE should hit Stripe endpoints
export const API_BASE = STRIPE_BASE;
export const SECURE_SPORTS_API_CONFIGURED = Boolean(SECURE_SPORTS_BASE_ENV);
export const SECURE_SPORTS_API_BASE = normalizeApiBase(
  SECURE_SPORTS_BASE_ENV || DEPLOYED_BACKEND
);

// Optional dev logging
if (!isProdHost) {
  // eslint-disable-next-line no-console
  console.log("[config] DATA_BASE =", DATA_BASE, "STRIPE_BASE =", STRIPE_BASE);
}
